import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import apiClient from '../utils/apiClient';

const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.lojenterprise.multiauthor';

const AGENTS = [
  { abbr: 'CS', name: 'Content Strategist', role: 'Strategy & Direction', bg: '#1E3A8A' },
  { abbr: 'RW', name: 'Research Writer', role: 'Research & Drafting', bg: '#1E3A8A' },
  { abbr: 'ME', name: 'Managing Editor', role: 'Editorial Oversight', bg: '#1E3A8A' },
  { abbr: 'SE', name: 'SEO Specialist', role: 'SEO & Copywriting', bg: '#F97316' },
  { abbr: 'FC', name: 'Fact-Checker', role: 'Accuracy & Verification', bg: '#1E3A8A' },
  { abbr: 'TL', name: 'Thought Leader', role: 'Conceptual Depth', bg: '#1E3A8A' },
  { abbr: 'SM', name: 'Subject Matter Expert', role: 'Domain Expertise', bg: '#1E3A8A' },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Brief The Scribe',
    desc: 'Answer a few questions. Our Intake Valet understands your topic, audience, tone, and goals — then builds a detailed brief for your AI team.',
  },
  {
    step: '02',
    title: 'Watch Your Team Work',
    desc: 'Seven specialised AI agents collaborate through a 9-step workflow. Watch them debate, draft, fact-check, and refine — live in your Studio.',
  },
  {
    step: '03',
    title: 'Publish With The Herald',
    desc: 'When your content is approved, The Herald formats and packages it for publication. Export, download, or push directly to your platform.',
  },
];

const PRICING = [
  {
    tier: 'Free',
    tierKey: '',
    price: '$0',
    period: '/month',
    desc: 'Explore AI-powered writing with your own OpenAI or OpenRouter key.',
    features: ['3 projects / month', 'All 9 workflow steps', 'All 14 content formats', 'Access 100+ models via OpenRouter', 'BYOK — bring your own key'],
    cta: 'Start Free',
    highlight: false,
  },
  {
    tier: 'Starter',
    tierKey: 'starter',
    price: '$9',
    period: '/month',
    desc: 'For solo creators building a consistent content habit.',
    features: ['18 projects / month', 'All 14 content formats', 'All 12 writing styles', 'Per-agent model selection', 'BYOK support', 'Email support'],
    cta: 'Get Starter',
    highlight: false,
  },
  {
    tier: 'Pro',
    tierKey: 'pro',
    price: '$29',
    period: '/month',
    desc: 'For professionals who create content at scale.',
    features: ['60 projects / month', 'All 14 content formats', 'All 12 writing styles', 'Per-agent model selection', 'BYOK support', 'Priority support'],
    cta: 'Start Pro',
    highlight: true,
  },
  {
    tier: 'Agency',
    tierKey: 'agency',
    price: '$79',
    period: '/month',
    desc: 'For teams producing content across multiple clients.',
    features: ['Unlimited projects', 'All 14 content formats', 'All 12 writing styles', 'Per-agent model selection', 'Team collaboration', 'Dedicated support'],
    cta: 'Start Agency',
    highlight: false,
  },
];

const FOUNDING_MEMBERS = [
  {
    tier: 'Lifetime Starter',
    price: '$99',
    period: 'one-time',
    desc: 'Lock in Starter access forever — no recurring fees.',
    features: ['Everything in Starter', 'Lifetime access', 'All future Starter features', 'Never pay again'],
    cta: 'Claim Lifetime Starter',
    highlight: false,
    stripeKey: 'lifetime_starter',
  },
  {
    tier: 'Lifetime Pro',
    price: '$249',
    period: 'one-time',
    desc: 'The best value for serious creators — pay once, create forever.',
    features: ['Everything in Pro', 'Lifetime access', 'All future Pro features', 'Priority support forever'],
    cta: 'Claim Lifetime Pro',
    highlight: true,
    stripeKey: 'lifetime_pro',
  },
  {
    tier: 'Lifetime Agency',
    price: '$599',
    period: 'one-time',
    desc: 'Agency power, forever. For teams that mean business.',
    features: ['Everything in Agency', 'Lifetime access', 'All future Agency features', 'Dedicated support forever'],
    cta: 'Claim Lifetime Agency',
    highlight: false,
    stripeKey: 'lifetime_agency',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((s) => s.auth);

  const [seatsRemaining, setSeatsRemaining] = useState(null);

  // Newsletter state
  const [nlEmail, setNlEmail] = useState('');
  const [nlState, setNlState] = useState('idle'); // idle | loading | success | error
  const [nlMsg, setNlMsg] = useState('');

  // Mobile menu state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    function onResize() {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) setMobileOpen(false);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    async function fetchSeats() {
      try {
        const r = await apiClient.get('/stripe/seats/');
        setSeatsRemaining(r.data.seats_remaining);
      } catch {}
    }
    fetchSeats();
    const id = setInterval(fetchSeats, 30000);
    return () => clearInterval(id);
  }, []);

  function handleMainCTA() {
    navigate(isAuthenticated ? '/task-creation' : '/register');
  }

  function scrollToPricing() {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  }

  async function handleNewsletter(e) {
    e.preventDefault();
    if (!nlEmail.trim()) return;
    setNlState('loading');
    try {
      await apiClient.post('/waitlist/', { name: '', email: nlEmail.trim(), company: '' });
      setNlState('success');
      setNlMsg("You're subscribed. We'll keep you updated.");
      setNlEmail('');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Something went wrong.';
      if (msg.toLowerCase().includes('already')) {
        setNlState('success');
        setNlMsg("You're already subscribed — we haven't forgotten you.");
      } else {
        setNlState('error');
        setNlMsg(msg);
      }
    }
  }

  const page = {
    background: '#0D1117',
    minHeight: '100vh',
    fontFamily: 'Inter, sans-serif',
    color: '#F1F5F9',
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100vh',
    WebkitOverflowScrolling: 'touch',
  };
  const nav = {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    background: 'rgba(13,17,23,0.92)', backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  };
  const navInner = {
    maxWidth: 1140, margin: '0 auto', padding: '0 16px',
    height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  };
  const logo = {
    fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#F1F5F9',
    display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
  };
  const ghostBtn = {
    padding: '6px 12px', background: 'transparent', color: '#CBD5E1',
    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, fontSize: 12,
    fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
  };
  const orangeBtn = {
    padding: '6px 14px', background: '#F97316', color: 'white',
    border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
  const section = (maxWidth = 900) => ({
    maxWidth, margin: '0 auto', padding: '80px 24px',
  });
  const card = {
    background: '#1A2236', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 28,
  };

  return (
    <div style={page}>
      {/* Nav */}
      <nav style={{ ...nav, position: 'fixed' }}>
        <div style={navInner}>
          <div style={logo}>
            <span>Multi</span>
            <span style={{ color: '#F97316' }}>Author</span>
          </div>
          {/* Desktop nav */}
          {!isMobile && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {isAuthenticated ? (
                <>
                  <button style={{ ...ghostBtn, fontSize: 12 }} onClick={() => navigate('/blogs')}>Blog</button>
                  <button style={{ ...ghostBtn, fontSize: 12 }} onClick={scrollToPricing}>Pricing</button>
                  <button style={orangeBtn} onClick={() => navigate('/dashboard')}>Dashboard</button>
                </>
              ) : (
                <>
                  <button style={{ ...ghostBtn, fontSize: 12 }} onClick={() => navigate('/blogs')}>Blog</button>
                  <button style={{ ...ghostBtn, fontSize: 12 }} onClick={scrollToPricing}>Pricing</button>
                  <button style={ghostBtn} onClick={() => navigate('/login')}>Sign In</button>
                  <button style={orangeBtn} onClick={() => navigate('/register')}>Get Started</button>
                </>
              )}
            </div>
          )}
          {/* Mobile hamburger */}
          {isMobile && (
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', padding: 8,
                display: 'flex', flexDirection: 'column', gap: 5, zIndex: 110,
              }}
              aria-label="Toggle menu"
            >
              <span style={{
                display: 'block', width: 22, height: 2, background: '#F1F5F9', borderRadius: 1,
                transform: mobileOpen ? 'rotate(45deg) translateY(7px)' : 'none',
                transition: 'transform 0.2s ease',
              }} />
              <span style={{
                display: 'block', width: 22, height: 2, background: '#F1F5F9', borderRadius: 1,
                opacity: mobileOpen ? 0 : 1, transition: 'opacity 0.2s ease',
              }} />
              <span style={{
                display: 'block', width: 22, height: 2, background: '#F1F5F9', borderRadius: 1,
                transform: mobileOpen ? 'rotate(-45deg) translateY(-7px)' : 'none',
                transition: 'transform 0.2s ease',
              }} />
            </button>
          )}
        </div>
        {/* Mobile dropdown menu */}
        {isMobile && mobileOpen && (
          <div style={{
            position: 'absolute', top: 56, left: 0, right: 0,
            background: 'rgba(13,17,23,0.98)', borderBottom: '1px solid rgba(255,255,255,0.06)',
            padding: '12px 16px 20px', display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            {isAuthenticated ? (
              <>
                <button style={{ ...ghostBtn, width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14 }} onClick={() => { setMobileOpen(false); navigate('/blogs'); }}>Blog</button>
                <button style={{ ...ghostBtn, width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14 }} onClick={() => { setMobileOpen(false); scrollToPricing(); }}>Pricing</button>
                <button style={{ ...orangeBtn, width: '100%', padding: '12px 16px', fontSize: 14 }} onClick={() => { setMobileOpen(false); navigate('/dashboard'); }}>Dashboard</button>
              </>
            ) : (
              <>
                <button style={{ ...ghostBtn, width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14 }} onClick={() => { setMobileOpen(false); navigate('/blogs'); }}>Blog</button>
                <button style={{ ...ghostBtn, width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14 }} onClick={() => { setMobileOpen(false); scrollToPricing(); }}>Pricing</button>
                <button style={{ ...ghostBtn, width: '100%', textAlign: 'left', padding: '12px 16px', fontSize: 14 }} onClick={() => { setMobileOpen(false); navigate('/login'); }}>Sign In</button>
                <button style={{ ...orangeBtn, width: '100%', padding: '12px 16px', fontSize: 14 }} onClick={() => { setMobileOpen(false); navigate('/register'); }}>Get Started</button>
              </>
            )}
          </div>
        )}
      </nav>

      {/* Hero */}
      <section style={{ ...section(860), paddingTop: 160, textAlign: 'center' }}>
        <div style={{
          display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: '#F97316',
          background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)',
          borderRadius: 999, padding: '5px 14px', marginBottom: 28,
        }}>
          ✦ AI-Powered Content Creation
        </div>
        <h1 style={{
          fontFamily: 'Syne, sans-serif', fontWeight: 800,
          fontSize: 'clamp(40px, 6vw, 72px)', lineHeight: 1.1,
          color: '#F1F5F9', marginBottom: 24,
        }}>
          Your AI Writing Team.<br />
          <span style={{ color: '#F97316' }}>Available 24/7.</span>
        </h1>
        <p style={{ fontSize: 18, color: '#9CA3AF', lineHeight: 1.7, maxWidth: 580, margin: '0 auto 40px' }}>
          Seven specialised agents, powered by 100+ AI models through OpenRouter —
          each agent can run on a different model, optimised for its role.
        </p>
        <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={handleMainCTA}
            style={{ ...orangeBtn, padding: '14px 32px', fontSize: 16, borderRadius: 12 }}
          >
            {isAuthenticated ? 'Create New Project' : 'Start Creating Free'}
          </button>
          <button
            onClick={scrollToPricing}
            style={{ ...ghostBtn, padding: '14px 32px', fontSize: 16, borderRadius: 12 }}
          >
            See Pricing
          </button>
        </div>
        {/* Google Play badge */}
        <div style={{ marginTop: 32 }}>
          <a
            href={GOOGLE_PLAY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: '#1A2236', border: '1px solid rgba(255,255,255,0.12)',
              borderRadius: 12, padding: '10px 20px', textDecoration: 'none',
              transition: 'border-color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(249,115,22,0.4)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
          >
            {/* Google Play icon */}
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 010 1.38l-2.302 2.302L15.395 13l2.303-3.492zM5.864 2.658L16.8 8.99l-2.302 2.302L5.864 2.658z" fill="#F97316"/>
            </svg>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 10, color: '#9CA3AF', lineHeight: 1.2 }}>GET IT ON</div>
              <div style={{ fontSize: 14, color: '#F1F5F9', fontWeight: 700, fontFamily: 'Syne, sans-serif', lineHeight: 1.2 }}>Google Play</div>
            </div>
          </a>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" style={{ ...section(1100), borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, color: '#F1F5F9', marginBottom: 12 }}>
            How It Works
          </h2>
          <p style={{ color: '#9CA3AF', fontSize: 16 }}>Three steps to production-ready content.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
          {HOW_IT_WORKS.map((item) => (
            <div key={item.step} style={card}>
              <div style={{
                fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 44,
                color: 'rgba(249,115,22,0.15)', marginBottom: 16,
              }}>{item.step}</div>
              <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 20, color: '#F1F5F9', marginBottom: 12 }}>
                {item.title}
              </h3>
              <p style={{ color: '#9CA3AF', fontSize: 14, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Agent Showcase */}
      <section style={{ ...section(1100), borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, color: '#F1F5F9', marginBottom: 12 }}>
            Meet Your AI Team
          </h2>
          <p style={{ color: '#9CA3AF', fontSize: 16 }}>7 specialised agents. Each can run on a different AI model. All working together.</p>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
          {AGENTS.map((agent) => (
            <div key={agent.name} style={{
              ...card, padding: '20px 18px', textAlign: 'center', minWidth: 130, flex: '1 1 130px', maxWidth: 160,
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14, background: agent.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontWeight: 800, fontSize: 14, margin: '0 auto 12px',
                fontFamily: 'Syne, sans-serif',
              }}>{agent.abbr}</div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#F1F5F9', marginBottom: 4 }}>{agent.name}</div>
              <div style={{ fontSize: 11, color: '#6B7280' }}>{agent.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* OpenRouter / Multi-Model USP */}
      <section style={{ ...section(1100), borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{
            display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: '#22C55E',
            background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)',
            borderRadius: 999, padding: '5px 14px', marginBottom: 20,
          }}>
            ✦ Powered by OpenRouter
          </div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, color: '#F1F5F9', marginBottom: 12 }}>
            Your Models. Your Rules.
          </h2>
          <p style={{ color: '#9CA3AF', fontSize: 16, maxWidth: 580, margin: '0 auto' }}>
            We've partnered with OpenRouter so you're not locked into one AI provider.
            Pick the best model for each agent — or let them all run on different ones.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
          {/* Card 1: Multi-Provider */}
          <div style={card}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16, fontSize: 20,
            }}>🌐</div>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 17, color: '#F1F5F9', marginBottom: 8 }}>
              100+ Models, One Platform
            </h3>
            <p style={{ color: '#9CA3AF', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              Access GPT-4o, Claude, Gemini, Llama, Mistral, DeepSeek, and more — all through a single integration. No juggling API keys across providers.
            </p>
          </div>
          {/* Card 2: Per-Agent Model Selection */}
          <div style={card}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16, fontSize: 20,
            }}>🎯</div>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 17, color: '#F1F5F9', marginBottom: 8 }}>
              Per-Agent Model Choice
            </h3>
            <p style={{ color: '#9CA3AF', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              Give your Content Strategist Claude while your Fact-Checker runs on GPT-4o. Each of the 7 agents can use a different model — optimised for its role.
            </p>
          </div>
          {/* Card 3: BYOK */}
          <div style={card}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginBottom: 16, fontSize: 20,
            }}>🔑</div>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 17, color: '#F1F5F9', marginBottom: 8 }}>
              Bring Your Own Key
            </h3>
            <p style={{ color: '#9CA3AF', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
              Use your own OpenAI or OpenRouter API key. You control your spend, your data, and which models power your team. Zero markup on tokens.
            </p>
          </div>
        </div>
        {/* Model logos row */}
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 28,
          flexWrap: 'wrap', marginTop: 40, opacity: 0.6,
        }}>
          {['OpenAI', 'Anthropic', 'Google', 'Meta', 'Mistral', 'DeepSeek'].map((name) => (
            <span key={name} style={{
              fontSize: 13, fontWeight: 700, color: '#6B7280',
              letterSpacing: '0.06em', textTransform: 'uppercase',
              fontFamily: 'Syne, sans-serif',
            }}>{name}</span>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" style={{ ...section(1200), borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, color: '#F1F5F9', marginBottom: 12 }}>
            Simple Pricing
          </h2>
          <p style={{ color: '#9CA3AF', fontSize: 16 }}>Start free. Scale when you're ready.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 18, alignItems: 'start' }}>
          {PRICING.map((plan) => (
            <div key={plan.tier} style={{
              ...card,
              border: plan.highlight ? '2px solid #F97316' : '1px solid rgba(255,255,255,0.08)',
              position: 'relative',
            }}>
              {plan.highlight && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: '#F97316', color: 'white', fontSize: 11, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 999, letterSpacing: '0.08em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  Most Popular
                </div>
              )}
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#F1F5F9' }}>{plan.tier}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, color: '#F1F5F9' }}>{plan.price}</span>
                <span style={{ color: '#6B7280', fontSize: 13 }}>{plan.period}</span>
              </div>
              <p style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 20 }}>{plan.desc}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#CBD5E1' }}>
                    <span style={{ color: '#F97316', fontWeight: 700 }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  if (plan.tierKey) {
                    navigate(isAuthenticated ? `/profile?plan=${plan.tierKey}` : `/register?plan=${plan.tierKey}`);
                  } else {
                    navigate(isAuthenticated ? '/task-creation' : '/register');
                  }
                }}
                style={{
                  width: '100%', padding: '12px 0',
                  background: plan.highlight ? '#F97316' : 'transparent',
                  color: plan.highlight ? 'white' : '#F97316',
                  border: plan.highlight ? 'none' : '1px solid rgba(249,115,22,0.4)',
                  borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  fontFamily: 'Syne, sans-serif',
                }}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Founding Member */}
      <section style={{ ...section(1200), borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{
            display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: '#F97316',
            background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)',
            borderRadius: 999, padding: '5px 14px', marginBottom: 20,
          }}>
            ♾ Founding Member — Limited to 100 Seats
          </div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, color: '#F1F5F9', marginBottom: 12 }}>
            Own It Forever
          </h2>
          <p style={{ color: '#9CA3AF', fontSize: 16, marginBottom: 16 }}>
            Pay once. Use Multi Author for life. No subscriptions. No renewals.
          </p>
          {seatsRemaining !== null && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: seatsRemaining <= 20 ? 'rgba(248,113,113,0.1)' : 'rgba(34,197,94,0.1)',
              border: `1px solid ${seatsRemaining <= 20 ? 'rgba(248,113,113,0.3)' : 'rgba(34,197,94,0.3)'}`,
              borderRadius: 999, padding: '6px 16px', marginBottom: 40,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: seatsRemaining <= 20 ? '#F87171' : '#22C55E',
              }} />
              <span style={{
                fontSize: 13, fontWeight: 700,
                color: seatsRemaining <= 20 ? '#F87171' : '#22C55E',
              }}>
                {seatsRemaining} / 100 seats remaining
              </span>
            </div>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18, alignItems: 'start' }}>
          {FOUNDING_MEMBERS.map((plan) => (
            <div key={plan.tier} style={{
              background: '#1A2236',
              border: plan.highlight ? '2px solid #F97316' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 16, padding: 28, position: 'relative',
            }}>
              {plan.highlight && (
                <div style={{
                  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                  background: '#F97316', color: 'white', fontSize: 11, fontWeight: 700,
                  padding: '3px 12px', borderRadius: 999, letterSpacing: '0.08em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap',
                }}>
                  Best Value
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17, color: '#F1F5F9' }}>{plan.tier}</span>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: '#F97316', background: 'rgba(249,115,22,0.1)', borderRadius: 999, padding: '2px 8px',
                }}>♾ Lifetime</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, color: '#F1F5F9' }}>{plan.price}</span>
                <span style={{ color: '#6B7280', fontSize: 13 }}>{plan.period}</span>
              </div>
              <p style={{ color: '#9CA3AF', fontSize: 13, marginBottom: 20 }}>{plan.desc}</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: 9 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#CBD5E1' }}>
                    <span style={{ color: '#F97316', fontWeight: 700 }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => {
                  if (plan.stripeKey) {
                    navigate(isAuthenticated ? `/profile?plan=${plan.stripeKey}` : `/register?plan=${plan.stripeKey}`);
                  } else {
                    navigate(isAuthenticated ? '/task-creation' : '/register');
                  }
                }}
                disabled={seatsRemaining === 0}
                style={{
                  width: '100%', padding: '12px 0',
                  background: plan.highlight ? '#F97316' : 'transparent',
                  color: plan.highlight ? 'white' : '#F97316',
                  border: plan.highlight ? 'none' : '1px solid rgba(249,115,22,0.4)',
                  borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: seatsRemaining === 0 ? 'not-allowed' : 'pointer',
                  opacity: seatsRemaining === 0 ? 0.5 : 1,
                  fontFamily: 'Syne, sans-serif',
                }}
              >
                {seatsRemaining === 0 ? 'Sold Out' : plan.cta}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section style={{ ...section(560), borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 36, color: '#F1F5F9', marginBottom: 12 }}>
            Stay in the Loop
          </h2>
          <p style={{ color: '#9CA3AF', fontSize: 16 }}>
            Get updates on new features, content tips, and early access to what's next.
          </p>
        </div>
        {nlState === 'success' ? (
          <div style={{
            ...card, textAlign: 'center', padding: 40,
            border: '1px solid rgba(249,115,22,0.3)',
          }}>
            <p style={{ color: '#F97316', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>{nlMsg}</p>
          </div>
        ) : (
          <form onSubmit={handleNewsletter} style={{ ...card, display: 'flex', flexDirection: 'row', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="email"
              placeholder="Your email address"
              required
              value={nlEmail}
              onChange={(e) => setNlEmail(e.target.value)}
              style={{
                flex: '1 1 240px', background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10,
                padding: '13px 16px', color: '#F1F5F9', fontSize: 15,
                outline: 'none', fontFamily: 'Inter, sans-serif',
              }}
            />
            <button
              type="submit"
              disabled={nlState === 'loading'}
              style={{
                ...orangeBtn, padding: '13px 28px', borderRadius: 10, fontSize: 15,
                fontFamily: 'Syne, sans-serif', opacity: nlState === 'loading' ? 0.7 : 1,
              }}
            >
              {nlState === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </button>
            {nlState === 'error' && (
              <p style={{ color: '#F87171', fontSize: 13, margin: 0, width: '100%' }}>{nlMsg}</p>
            )}
          </form>
        )}
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '32px 24px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 16, color: '#F1F5F9' }}>
            Multi<span style={{ color: '#F97316' }}>Author</span>
            <span style={{ color: '#4B5563', fontWeight: 400, fontSize: 13, marginLeft: 12 }}>
              © {new Date().getFullYear()} L.O.J Enterprise
            </span>
          </div>
          <div style={{ display: 'flex', gap: 24 }}>
            {[
              ['Blog', '/blogs'],
              ['Pricing', null],
              ['Sign In', '/login'],
              ['Register', '/register'],
              ['Privacy', '/privacy'],
            ].map(([label, path]) => (
              <button key={label} onClick={() => path ? navigate(path) : scrollToPricing()} style={{
                background: 'none', border: 'none', color: '#6B7280', fontSize: 13, cursor: 'pointer',
              }}>{label}</button>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
